/**
 * Pure dependency-graph helpers for the fleet console's module editor.
 *
 * The graph itself is NEVER hardcoded here — every function takes a
 * `ModuleCatalogEntry[]` served by `GET /api/fleet/instances`, which the
 * backend derives from `config/modules.ts`'s `MODULES`. That is the whole
 * point: the closure this file walks and the closure
 * `resolveEnabledModules` walks server-side are the same edges, so the
 * editor's checkbox behaviour cannot drift from the set the server will
 * actually resolve.
 *
 * SCOPE (P-45): these are SELECTION aids. Nothing here decides whether a
 * module set may be saved — that verdict comes only from the server's
 * preview response. `selectWithDependencies`/`deselectWithDependents` shape
 * what the operator is asking for; the server independently re-resolves it
 * and can still refuse.
 *
 * Every function tolerates a catalog that is empty, partial, or contains a
 * dependency edge pointing at an id it does not list — the editor falls
 * back to a flat, dependency-free checklist in that case rather than
 * throwing inside a render.
 */
import type { ModuleCatalogEntry } from '../services/api/fleet';
import type { ModuleId } from '../services/api/modules';

/** One node of the display forest. */
export interface ModuleTreeNode {
  id: ModuleId;
  label: string;
  /** Unique per NODE, not per module — a module with two display parents
   * appears twice and the two occurrences need distinct React keys. */
  path: string;
  depth: number;
  /** Labels of this module's OTHER display parents, so a duplicated row can
   * say where else it appears. Empty for single-parent modules. */
  alsoUnder: string[];
  children: ModuleTreeNode[];
}

type Catalog = readonly ModuleCatalogEntry[];

function indexById(catalog: Catalog): Map<ModuleId, ModuleCatalogEntry> {
  return new Map(catalog.map((entry) => [entry.id, entry]));
}

/**
 * The parents a module is DISPLAYED under, which is not quite its
 * dependency list.
 *
 * `core` is the always-on root every dependency chain reaches, so an edge to
 * it carries no information once a module has some other parent: showing
 * `people` (deps: core, time) under both `core` and `time` doubles the row
 * for nothing. A module whose ONLY dependency is `core` still hangs off it —
 * that edge is all it has.
 */
export function displayParents(entry: ModuleCatalogEntry): ModuleId[] {
  if (entry.deps.length <= 1) return [...entry.deps];
  const withoutCore = entry.deps.filter((dep) => dep !== 'core');
  return withoutCore.length > 0 ? withoutCore : [...entry.deps];
}

/**
 * Builds the nested display forest. Multi-parent modules (`capacity`, whose
 * deps are `projects` + `people`) are DUPLICATED — one node under each
 * display parent — and the editor keeps the duplicate rows' checkbox state
 * in sync because they are the same module, not two.
 *
 * A module whose display parents are all missing from the catalog becomes a
 * root, so a partial catalog still renders every module it lists. The
 * `ancestors` guard means a malformed catalog containing a cycle produces a
 * truncated tree instead of blowing the stack.
 */
export function buildModuleForest(catalog: Catalog): ModuleTreeNode[] {
  const byId = indexById(catalog);
  const parentsOf = new Map<ModuleId, ModuleId[]>();
  for (const entry of catalog) {
    parentsOf.set(
      entry.id,
      displayParents(entry).filter((dep) => byId.has(dep)),
    );
  }

  const labelFor = (id: ModuleId) => byId.get(id)?.label ?? id;

  function build(id: ModuleId, parentPath: string, depth: number, ancestors: Set<ModuleId>): ModuleTreeNode {
    const parents = parentsOf.get(id) ?? [];
    const path = parentPath ? `${parentPath}/${id}` : id;
    const nextAncestors = new Set(ancestors).add(id);
    const children = catalog
      .filter((candidate) => (parentsOf.get(candidate.id) ?? []).includes(id))
      .filter((candidate) => !nextAncestors.has(candidate.id))
      .map((candidate) => build(candidate.id, path, depth + 1, nextAncestors));

    return {
      id,
      label: labelFor(id),
      path,
      depth,
      alsoUnder:
        parents.length > 1
          ? parents.filter((parent) => parent !== lastSegment(parentPath)).map(labelFor)
          : [],
      children,
    };
  }

  return catalog
    .filter((entry) => (parentsOf.get(entry.id) ?? []).length === 0)
    .map((entry) => build(entry.id, '', 0, new Set()));
}

function lastSegment(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] ?? '';
}

/** Flattens the forest into render order (pre-order, parents before children). */
export function flattenForest(nodes: readonly ModuleTreeNode[]): ModuleTreeNode[] {
  const out: ModuleTreeNode[] = [];
  for (const node of nodes) {
    out.push(node);
    out.push(...flattenForest(node.children));
  }
  return out;
}

/** Every module `id` depends on, transitively. Excludes `id` itself. */
export function transitiveDependencies(id: ModuleId, catalog: Catalog): Set<ModuleId> {
  const byId = indexById(catalog);
  const found = new Set<ModuleId>();
  const stack: ModuleId[] = [...(byId.get(id)?.deps ?? [])];
  while (stack.length > 0) {
    const next = stack.pop() as ModuleId;
    if (next === id || found.has(next) || !byId.has(next)) continue;
    found.add(next);
    stack.push(...(byId.get(next)?.deps ?? []));
  }
  return found;
}

/**
 * Every module that would drag `id` back in through its own dependency
 * chain — the reverse closure. Mirrors `dependentsOf` in
 * `backend/src/config/modules.ts`.
 */
export function transitiveDependents(id: ModuleId, catalog: Catalog): Set<ModuleId> {
  const found = new Set<ModuleId>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const entry of catalog) {
      if (entry.id === id || found.has(entry.id)) continue;
      if (entry.deps.includes(id) || entry.deps.some((dep) => found.has(dep))) {
        found.add(entry.id);
        changed = true;
      }
    }
  }
  return found;
}

/** `selected` plus `id` plus everything `id` needs. */
export function selectWithDependencies(
  selected: ReadonlySet<ModuleId>,
  id: ModuleId,
  catalog: Catalog,
): Set<ModuleId> {
  const next = new Set(selected);
  next.add(id);
  for (const dep of transitiveDependencies(id, catalog)) next.add(dep);
  return next;
}

/**
 * `selected` minus `id` minus everything that depends on it.
 *
 * Dropping only `id` would be theatre: the server's forward closure re-adds
 * it on behalf of any remaining dependent, so the checkbox would appear to
 * turn off and the saved set would be unchanged.
 */
export function deselectWithDependents(
  selected: ReadonlySet<ModuleId>,
  id: ModuleId,
  catalog: Catalog,
): Set<ModuleId> {
  const next = new Set(selected);
  next.delete(id);
  for (const dependent of transitiveDependents(id, catalog)) next.delete(dependent);
  return next;
}

/**
 * The modules a toggle changed BEYOND the one that was clicked — what the
 * editor reports as "Also turned on/off". Returned in catalog order so the
 * sentence is deterministic regardless of Set iteration order.
 */
export function collateralChanges(
  before: ReadonlySet<ModuleId>,
  after: ReadonlySet<ModuleId>,
  clicked: ModuleId,
  catalog: Catalog,
): { turnedOn: ModuleId[]; turnedOff: ModuleId[] } {
  const ordered = catalog.map((entry) => entry.id);
  return {
    turnedOn: ordered.filter((id) => id !== clicked && after.has(id) && !before.has(id)),
    turnedOff: ordered.filter((id) => id !== clicked && before.has(id) && !after.has(id)),
  };
}
